import { REALTIME_ORIGIN } from "@birdwatch/client";
import {
  isRealtimeChannelAllowed,
  type RealtimeEventName,
} from "@birdwatch/common";
import type { QueryKey } from "@tanstack/react-query";
import { useContext } from "react";

import { useRealtimeRelay } from "@/features/shared/realtime-relay";
import { RealtimeRefreshContext } from "./realtime-refresh-root";
import { REALTIME_REFRESH_EVENTS, REALTIME_REFRESH_KEYS } from "./refresh-keys";

/**
 * Subscribe to channels and refresh, through the shared refresh map, whatever
 * their events make stale. An event carrying this tab's own origin is the echo of
 * a write whose `onSuccess` already refreshed; it only refreshes queries that are
 * neither fetching nor updated since this tab's write started (see
 * `_libs/echo-filter.ts`).
 *
 * One write heard on several channels (an `order.changed` on the order AND the
 * home) refreshes each query once: events carry the id of the write that caused
 * them, and the queue remembers what it refreshed for each write.
 *
 * Every event is a hint, never data: the refetch goes through the API, which
 * enforces visibility, and a replayed event (the relay replays recent history on
 * reconnect) just refreshes again.
 *
 * A channel the relay would refuse (a malformed or missing id) is dropped here:
 * every hook on the page shares one stream, and one refused channel would stop
 * all of them.
 *
 * @returns The relay connection status.
 */
export function useRealtimeRefresh({
  channels,
  enabled = true,
}: {
  channels: readonly (string | null | undefined)[];
  enabled?: boolean;
}) {
  const scheduler = useContext(RealtimeRefreshContext);
  if (!scheduler) {
    throw new Error(
      "useRealtimeRefresh must be used inside <RealtimeRefreshRoot>",
    );
  }
  return useRealtimeRelay({
    channels: channels.filter(
      (channel): channel is string =>
        Boolean(channel) && isRealtimeChannelAllowed(channel as string),
    ),
    enabled,
    events: REALTIME_REFRESH_EVENTS,
    onData: (payload) => {
      // The relay narrows `data` per event; the map is indexed by the same name.
      const keysFor = REALTIME_REFRESH_KEYS[
        payload.event as RealtimeEventName
      ] as (data: unknown) => QueryKey[];
      scheduler.schedule(keysFor(payload.data), {
        ownEcho: payload.origin === REALTIME_ORIGIN,
        writeId: payload.writeId,
      });
    },
  });
}
